import { Subscription, timer, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Input,
  Component,
  OnDestroy,
  ElementRef,
  HostBinding,
  HostListener,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SnAspectRatio } from '@lib/common/enums';

import { SnPlayerState } from '../../enums';

@Component({
  selector: 'sn-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sn-player',
  },
})
export class SnPlayerComponent implements OnDestroy {
  @Input()
  public caption: string;

  @Input()
  public elevation: number;

  @Input()
  public fullscreen: boolean;

  @Input()
  public isLocal: boolean;

  @Input()
  public aspectRatio: SnAspectRatio;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('width')
  set widthConfig(value: string | number) {
    this._width = typeof value === 'number' ? `${value}px` : value;
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('height')
  set heightConfig(value: string | number) {
    this._height = typeof value === 'number' ? `${value}px` : value;
  }

  @Input()
  set state(value: SnPlayerState) {
    if (this._state !== value) {
      this._elementRef.nativeElement.classList.remove(`sn-player_${this._state}`);
      this._elementRef.nativeElement.classList.add(`sn-player_${value}`);
      this._state = value;
    }
  }

  private _width: string;
  private _height: string;

  private _mouseIn = false;
  private _mouseInActive = false;
  private _mouseInActiveTimer: Subscription;

  private _state: SnPlayerState;

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _elementRef: ElementRef) {}

  @HostBinding('style.width')
  get width(): string {
    return this._width;
  }

  @HostBinding('style.height')
  get height(): string {
    return this._height;
  }

  @HostBinding('class.mouse-in')
  get mouseIn(): boolean {
    return this._mouseIn;
  }

  @HostBinding('class.mouse-in-active')
  get mouseInActive(): boolean {
    return this._mouseInActive;
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  @HostListener('mousemove')
  public onMouseMove(): void {
    this._mouseIn = true;
    this._mouseInActive = true;

    if (this._mouseInActiveTimer) {
      this._mouseInActiveTimer.unsubscribe();
      this._mouseInActiveTimer = null;
    }

    this._mouseInActiveTimer = timer(20000)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => (this._mouseInActive = false));
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    this._mouseIn = false;
    this._mouseInActive = false;

    if (this._mouseInActiveTimer) {
      this._mouseInActiveTimer.unsubscribe();
      this._mouseInActiveTimer = null;
    }
  }

  public round(meters: number): number {
    return Math.round(meters * 10) / 10;
  }
}
